// Single-purpose overlay: "tap to go fullscreen" + (when available) an
// "Install app" call-to-action that triggers Chrome's PWA install flow.
//
// Lifecycle:
//   - On mount: if we're already running as an installed PWA (fullscreen or
//     standalone display-mode) → render nothing. Otherwise → render overlay.
//   - On entering fullscreen → hide.
//   - On exiting fullscreen (Esc, F11) → re-show.
//   - On `beforeinstallprompt` → stash the event; show the Install button.
//
// Both buttons are user-gesture handlers, which is what the browser requires
// for requestFullscreen() and the PWA install prompt.

import { useEffect, useRef, useState } from 'react'
import {
  enterFullscreen,
  isFullscreen,
  isStandaloneOrFullscreenPwa,
  onFullscreenChange,
} from '../lib/fullscreen'

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>
}

interface Props {
  // Suppress the prompt while the player is mid-loading (initial fetch,
  // post-setup handoff, or publish in flight). The brand mark is the focal
  // point during loading; the fullscreen CTA reappears once the player has
  // settled on a real state screen or content.
  hidden?: boolean
  // Reports whether the overlay is actually rendering, so the layout under
  // it (the centered state screens) can reserve bottom space instead of
  // letting the prompt cover the pairing steps on short/portrait viewports.
  onVisibleChange?: (visible: boolean) => void
  // Reports the overlay's own rendered height in px so the caller reserves
  // exactly that much space — not a fixed guess. A guessed constant either
  // wastes room on tall viewports or, worse, outsizes what's actually needed
  // on short ones and pushes real content (e.g. the pairing steps) into a
  // scrollbar that shouldn't exist.
  onHeightChange?: (px: number) => void
  // Operator setting (display_settings.hide_player_controls). Suppresses the
  // overlay outright for screens that are already launched fullscreen or
  // installed as a PWA, where it is just chrome sitting on paid content.
  suppressed?: boolean
}

export default function FullscreenPrompt({ hidden = false, suppressed = false, onVisibleChange, onHeightChange }: Props) {
  // Installed-PWA users never see this. Compute once on mount so we don't
  // flash the overlay between paints.
  const [hideForever, setHideForever] = useState(isStandaloneOrFullscreenPwa)
  const [inFullscreen, setInFullscreen] = useState(isFullscreen)
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (hideForever) return
    const off = onFullscreenChange(() => setInFullscreen(isFullscreen()))
    const onBeforeInstall = (e: Event) => {
      e.preventDefault()
      setInstallEvent(e as BeforeInstallPromptEvent)
    }
    const onInstalled = () => setHideForever(true)
    window.addEventListener('beforeinstallprompt', onBeforeInstall)
    window.addEventListener('appinstalled', onInstalled)
    return () => {
      off()
      window.removeEventListener('beforeinstallprompt', onBeforeInstall)
      window.removeEventListener('appinstalled', onInstalled)
    }
  }, [hideForever])

  const visible = !(hideForever || inFullscreen || hidden || suppressed)
  useEffect(() => { onVisibleChange?.(visible) }, [visible, onVisibleChange])

  // Measure the overlay's real height (it wraps text, so this varies with
  // viewport width) and report it up so the reserved space always matches
  // what's actually on screen instead of a worst-case constant.
  useEffect(() => {
    if (!visible) { onHeightChange?.(0); return }
    const el = rootRef.current
    if (!el) return
    const report = () => onHeightChange?.(el.offsetHeight)
    report()
    const ro = new ResizeObserver(report)
    ro.observe(el)
    return () => ro.disconnect()
  }, [visible, onHeightChange, installEvent])

  if (!visible) return null

  const goFullscreen = () => { void enterFullscreen() }

  const install = async () => {
    if (!installEvent) return
    await installEvent.prompt()
    const choice = await installEvent.userChoice
    if (choice.outcome === 'accepted') setInstallEvent(null)
  }

  return (
    <div
      ref={rootRef}
      // Center-bottom overlay; semitransparent backdrop so the prompt is
      // legible even when the screen is showing bright content behind it.
      // pointer-events on the wrapper, not the children, so taps anywhere
      // outside the buttons are ignored (kiosks get bumped a lot).
      className="absolute inset-x-0 bottom-0 z-20 flex justify-center px-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:pb-10 pointer-events-none"
    >
      <div
        // No background container and no copy — just the buttons, floating
        // directly over the screen. This overlay sits on top of paying content,
        // so it carries the minimum that still works: the labels ("Go
        // fullscreen" / "Install app") already say what each button does, and
        // the surrounding prose only added height over the customer's content.
        className="pointer-events-auto flex w-full max-w-sm flex-col items-center gap-3 px-3 py-4 sm:px-6 sm:py-5"
      >
        <div className="flex w-full flex-col items-stretch gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
          <button
            type="button"
            onClick={goFullscreen}
            className="min-h-10 px-5 py-2 rounded-lg bg-brand text-white text-sm font-semibold hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand"
          >
            Go fullscreen
          </button>
          {installEvent && (
            <button
              type="button"
              onClick={install}
              className="min-h-10 px-5 py-2 rounded-lg bg-surface text-white text-sm font-semibold hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
            >
              Install app
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
